import React, { useEffect, useState } from 'react'
import { Loader2, Sparkles, X } from 'lucide-react'
import toast from 'react-hot-toast'
import { useTranslation } from '../../hooks/use-i18n'
import agentService, { AgentEnrollmentProposal } from '../../services/agentService'
import {
  enrollPublicParticipant,
  enrollPublicParticipantBulk,
  getCountries,
  getCitiesByCountry,
} from '../../services/publicService'
import type { CityItem, CountryItem } from '../../types/public'

type GameType = 'football' | 'formula1'

export type ConciergeEnrollExtras = {
  tournamentIds: string[]
  countryCode: string
  city: string
}

interface OnboardingConciergeModalProps {
  onClose: () => void
  onEnrolled: (gameType: GameType, extras?: ConciergeEnrollExtras) => void
  onOpenCreateRoom?: () => void
  onlyGameType?: GameType
}

const OnboardingConciergeModal: React.FC<OnboardingConciergeModalProps> = ({
  onClose,
  onEnrolled,
  onOpenCreateRoom,
  onlyGameType,
}) => {
  const { t, locale } = useTranslation()
  const [enabled, setEnabled] = useState<boolean | null>(null)
  const [gameType, setGameType] = useState<GameType>(onlyGameType ?? 'football')
  const [countries, setCountries] = useState<CountryItem[]>([])
  const [cities, setCities] = useState<CityItem[]>([])
  const [countryCode, setCountryCode] = useState('')
  const [city, setCity] = useState('')
  const [loadingCities, setLoadingCities] = useState(false)
  const [message, setMessage] = useState('')
  const [userQuestion, setUserQuestion] = useState('')
  const [sources, setSources] = useState<string[]>([])
  const [fromTemplate, setFromTemplate] = useState(false)
  const [proposeEnrollment, setProposeEnrollment] = useState<AgentEnrollmentProposal | null>(null)
  const [suggestCreateRoom, setSuggestCreateRoom] = useState(false)
  const [loading, setLoading] = useState(false)
  const [enrolling, setEnrolling] = useState(false)

  useEffect(() => {
    agentService.getStatus().then((s) => setEnabled(s.enabled)).catch(() => setEnabled(false))
    getCountries().then(setCountries).catch(() => setCountries([]))
  }, [])

  useEffect(() => {
    if (!countryCode.trim()) {
      setCities([])
      setCity('')
      return
    }
    let cancelled = false
    setLoadingCities(true)
    setCity('')
    getCitiesByCountry(countryCode)
      .then((list) => { if (!cancelled) setCities(list) })
      .catch(() => { if (!cancelled) setCities([]) })
      .finally(() => { if (!cancelled) setLoadingCities(false) })
    return () => { cancelled = true }
  }, [countryCode])

  const fetchGuidance = async () => {
    setLoading(true)
    setProposeEnrollment(null)
    try {
      const res = await agentService.onboardingConcierge({
        gameType,
        message: userQuestion.trim() || undefined,
        countryCode: countryCode.trim() || undefined,
        city: city.trim() || undefined,
        locale,
      })
      setMessage(res.message)
      setSources(res.sources ?? [])
      setFromTemplate(res.fromTemplate)
      setProposeEnrollment(res.proposeEnrollment ?? null)
      setSuggestCreateRoom(Boolean(res.suggestCreateRoom))
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        t('agent.errorGeneric')
      toast.error(typeof msg === 'string' ? msg : t('agent.errorGeneric'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (enabled) void fetchGuidance()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled])

  const handleConfirmEnrollment = async () => {
    if (!proposeEnrollment) return
    setEnrolling(true)
    try {
      const { tournamentIds, countryCode: cc, city: c } = proposeEnrollment
      if (tournamentIds.length > 1) {
        await enrollPublicParticipantBulk({
          tournamentIds,
          countryCode: cc,
          city: c,
        })
      } else {
        await enrollPublicParticipant({
          publicTournamentId: tournamentIds[0],
          countryCode: cc,
          city: c,
        })
      }
      toast.success(t('playPublic.enrolled'))
      onEnrolled(proposeEnrollment.gameType, {
        tournamentIds,
        countryCode: cc,
        city: c,
      })
      onClose()
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        t('playPublic.enrollFailed')
      toast.error(typeof msg === 'string' ? msg : t('playPublic.enrollFailed'))
    } finally {
      setEnrolling(false)
    }
  }

  if (enabled === false) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
        <div className="glass-card rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
          <p className="text-muted-foreground text-sm">{t('agent.conciergeDisabled')}</p>
          <button type="button" onClick={onClose} className="mt-4 btn-secondary px-4 py-2 rounded-xl w-full">
            {t('common.close')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div
        className="glass-card rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start gap-2 mb-4">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
            <h2 className="font-display text-xl font-bold text-foreground">{t('agent.conciergeTitle')}</h2>
            <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
              {t('agent.aiBadge')}
            </span>
          </div>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground p-1" aria-label={t('agent.close')}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {!onlyGameType && (
          <div className="grid grid-cols-2 gap-2 mb-4">
            {(['football', 'formula1'] as GameType[]).map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => setGameType(type)}
                className={`rounded-xl py-2.5 flex items-center justify-center gap-2 text-sm font-medium border transition-all ${
                  gameType === type
                    ? 'border-primary bg-primary/10 text-foreground'
                    : 'border-muted/20 text-muted-foreground'
                }`}
              >
                <span>{type === 'football' ? '⚽' : '🏎️'}</span>
                {type === 'football' ? t('dashboard.worldFootball') : t('dashboard.worldFormula1')}
              </button>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">{t('playPublic.country')}</label>
            <select
              value={countryCode}
              onChange={(e) => setCountryCode(e.target.value)}
              className="w-full rounded-lg border border-muted/30 bg-background px-3 py-2 text-sm"
            >
              <option value="">{t('playPublic.selectCountry')}</option>
              {countries.map((c) => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">{t('playPublic.city')}</label>
            <select
              value={city}
              onChange={(e) => setCity(e.target.value)}
              disabled={!countryCode || loadingCities}
              className="w-full rounded-lg border border-muted/30 bg-background px-3 py-2 text-sm disabled:opacity-50"
            >
              <option value="">{loadingCities ? t('common.loading') : t('playPublic.selectCity')}</option>
              {cities.map((c) => (
                <option key={c.id} value={c.cityName}>{c.cityName}</option>
              ))}
            </select>
          </div>
        </div>

        <input
          type="text"
          value={userQuestion}
          onChange={(e) => setUserQuestion(e.target.value)}
          placeholder={t('agent.conciergeQuestionPlaceholder')}
          className="w-full mb-3 px-3 py-2 rounded-lg border border-muted/30 bg-background text-sm"
        />

        <button
          type="button"
          onClick={() => void fetchGuidance()}
          disabled={loading}
          className="w-full mb-4 px-4 py-2.5 rounded-xl bg-primary text-primary-foreground font-medium text-sm disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          {t('agent.conciergeGetGuidance')}
        </button>

        {message && !loading && (
          <div className="text-sm text-foreground whitespace-pre-wrap leading-relaxed mb-3 rounded-xl bg-muted/20 p-3">
            {message}
          </div>
        )}

        {sources.length > 0 && !loading && (
          <p className="text-xs text-muted-foreground mb-3">
            {t('agent.sources')}: {sources.join(' · ')}
            {fromTemplate ? ` · ${t('agent.templateFallback')}` : ''}
          </p>
        )}

        <div className="flex flex-col sm:flex-row gap-2">
          {proposeEnrollment && (
            <button
              type="button"
              onClick={() => void handleConfirmEnrollment()}
              disabled={enrolling}
              className="flex-1 px-4 py-2.5 rounded-xl bg-green-800 text-white font-semibold text-sm hover:bg-green-700 disabled:opacity-50"
            >
              {enrolling ? t('common.loading') : t('agent.confirmEnrollment')}
            </button>
          )}
          {suggestCreateRoom && onOpenCreateRoom && (
            <button
              type="button"
              onClick={() => {
                onClose()
                onOpenCreateRoom()
              }}
              className="flex-1 px-4 py-2.5 rounded-xl border border-muted/30 text-sm font-medium"
            >
              {t('dashboard.createRoom')}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default OnboardingConciergeModal
